import type {
  GenerateReportParams,
  Report,
  ReportSection,
  ReportWithSections,
  ShareReportResponse,
} from '../types/report';
import baseApi from './baseApi';

type UpdateSectionParams = {
  reportId: number;
  sectionId: number;
  title?: string | null;
  subtitle?: string | null;
  text?: string | null;
  reason?: string | null;
  isHidden?: boolean;
  width?: number;
  height?: number;
};

type DeleteSectionParams = {
  reportId: number;
  sectionId: number;
};

type GetReportsParams = {
  paginationModel?: { page: number; pageSize: number };
  businessUnitId?: number | null;
};

type GetReportsResponse = {
  reports: Report[];
  total: number;
};

const reportsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getReports: build.query<GetReportsResponse, GetReportsParams | undefined>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.paginationModel) searchParams.set('paginationModel', JSON.stringify(params.paginationModel));
        if (params?.businessUnitId != null && params.businessUnitId !== -1) {
          searchParams.set('businessUnitId', String(params.businessUnitId));
        }
        const query = searchParams.toString();
        return {
          url: query ? `/reports?${query}` : '/reports',
          method: 'GET',
        };
      },
      providesTags: ['Reports'],
    }),
    getReport: build.query<ReportWithSections, number>({
      query: (id: number) => ({
        url: `/reports/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: ReportWithSections): ReportWithSections => {
        // If sections exist, sort them by 'order'
        if (response.sections && Array.isArray(response.sections)) {
          return {
            ...response,
            sections: [...response.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
          };
        }
        return response;
      },
      providesTags: (_result, _error, id) => [{ type: 'Reports', id }],
    }),
    generateReport: build.mutation<Report, GenerateReportParams>({
      query: (params: GenerateReportParams) => ({
        url: '/reports/generate',
        method: 'POST',
        body: params,
      }),
      invalidatesTags: ['Reports'],
    }),
    retryReport: build.mutation<Report, number>({
      query: (id: number) => ({
        url: `/reports/${id}/retry`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => ['Reports', { type: 'Reports', id }],
    }),
    deleteReport: build.mutation<void, number>({
      query: (id: number) => ({
        url: `/reports/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Reports'],
    }),
    updateSection: build.mutation<ReportSection, UpdateSectionParams>({
      query: ({ reportId, sectionId, ...body }) => ({
        url: `/reports/${reportId}/sections/${sectionId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { reportId }) => [{ type: 'Reports', id: reportId }],
    }),
    deleteSection: build.mutation<void, DeleteSectionParams>({
      query: ({ reportId, sectionId }) => ({
        url: `/reports/${reportId}/sections/${sectionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { reportId }) => [{ type: 'Reports', id: reportId }],
    }),
    shareReport: build.mutation<ShareReportResponse, number>({
      query: (reportId: number) => ({
        url: `/reports/${reportId}/share`,
        method: 'POST',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetReportsQuery,
  useGetReportQuery,
  useGenerateReportMutation,
  useRetryReportMutation,
  useDeleteReportMutation,
  useUpdateSectionMutation,
  useDeleteSectionMutation,
  useShareReportMutation,
} = reportsApi;
